// Helpers for the multi-gym account switcher.
// The active gym's details live at the top level of userInfo; every other
// logged-in gym is kept as a full snapshot inside userInfo.myGyms.

export const getGymKey = (info) => info?.gymId?._id;

// Heavy per-gym caches that can be dropped from parked snapshots when
// localStorage runs out of space. Dropping them forces a refetch after
// switching, so their "last retrieved" markers must be dropped too.
export const HEAVY_FIELDS = [
  "allUsers",
  "userAnalytics",
  "wsMsgs",
  "lastRetrivedDate_allUsers",
  "lastRetrieved_userAnalytics",
  "lastRetrivedDate_wsMsgs",
];

export const stripHeavyFields = (gymInfo) => {
  const light = { ...gymInfo };
  HEAVY_FIELDS.forEach((field) => delete light[field]);
  return light;
};

// Snapshot of the active gym, safe to park inside myGyms (no nesting).
export const snapshotActiveGym = (userInfo) => {
  const snapshot = { ...userInfo };
  delete snapshot.myGyms;
  return snapshot;
};

// userInfo after switching to a gym that is already inside myGyms.
// Returns null when the target gym is not found.
export const buildSwitchedUserInfo = (userInfo, targetGymMongoId) => {
  const myGyms = userInfo?.myGyms || [];
  const target = myGyms.find((gym) => getGymKey(gym) === targetGymMongoId);
  if (!target) return null;

  const current = snapshotActiveGym(userInfo);
  const updatedMyGyms = myGyms
    .filter((gym) => getGymKey(gym) !== targetGymMongoId)
    .filter((gym) => getGymKey(gym) !== getGymKey(current))
    .concat([current]);

  return { ...target, myGyms: updatedMyGyms };
};

// userInfo after a fresh "Login to Another Gym" succeeds. The current gym is
// parked (replacing any stale copy of itself) and any stale copy of the
// newly logged-in gym is dropped, so re-logging into the same gym never
// duplicates it.
// userInfo after logging out ONLY the active gym: the chosen parked gym
// (most recently used one by default) becomes active and the logged-out gym
// is dropped entirely — it is NOT parked back. Returns null when no parked
// gym is available (caller should fall back to a full logout).
export const buildLogoutUserInfo = (userInfo, targetGymMongoId) => {
  const myGyms = userInfo?.myGyms || [];
  if (!myGyms.length) return null;

  const key = targetGymMongoId ?? getGymKey(myGyms[myGyms.length - 1]);
  const target = myGyms.find((gym) => getGymKey(gym) === key);
  if (!target) return null;

  const remaining = myGyms
    .filter((gym) => getGymKey(gym) !== key)
    // safety: never resurrect the gym being logged out
    .filter((gym) => getGymKey(gym) !== getGymKey(userInfo));

  return { ...target, myGyms: remaining };
};

export const buildLoggedInUserInfo = (userInfo, newGymInfo) => {
  const myGyms = userInfo?.myGyms || [];
  const current = snapshotActiveGym(userInfo);

  const updatedMyGyms = myGyms
    .filter((gym) => getGymKey(gym) !== getGymKey(current))
    .concat([current])
    .filter((gym) => getGymKey(gym) !== getGymKey(newGymInfo));

  return { ...newGymInfo, myGyms: updatedMyGyms };
};
